import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import { useTranslation } from 'react-i18next';
import ProfileDropdown from 'components/ProfileDropdown';
import AuthContext from 'providers/Auth/Auth.context';
import MessagingAndNotification from '../MessagingAndNotification';
import AddProfile from 'icons/AddProfile.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import DropdownsOnHoverInstance from './components/DropdownsOnHoverInstance';
import BagIcon from 'icons/Bag.icon';
import CollectionIcon from 'icons/Collection.icon';
import SolutionsIcon from 'icons/Solutions.icon';
import {
  businessLink,
  buyingLink,
  dealsLinks,
  headerListingLinks,
  newsLink,
  sellingLink,
  solutionsLinks,
  toLandingLinks,
} from 'components/ResolveHeader/utils';
import { SearchForm } from '../SearchForm';
import NewsIcon from 'icons/News.icon';
import { getResolveUrl, isDefaultSellingPortfolio } from 'utils';
import { LOGIN_ROUTE, SIGNUP_ROUTE } from 'data/global';
import { BrandingLogos } from 'types';

import './ResolveHeaderDesktop.styles.scss';

type ResolveHeaderDesktopProps = {
  className?: string;
  brandingLogos?: BrandingLogos;
  hasSearch: boolean;
  hasMessagingLink: boolean;
};

const ResolveHeaderDesktop: React.FC<ResolveHeaderDesktopProps> = (props) => {
  const { className, hasSearch, hasMessagingLink, brandingLogos } = props;

  const { isLoggedIn } = useContext(AuthContext);
  const { isSuperAdmin, currentUser, hasEnterprise } =
    useContext(CurrentUserContext);

  const { hasSellingDeals } = currentUser || {};

  const classes = classNames('resolve-header-desktop', className);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const RenderLandingLinks = useMemo(
    () => (
      <>
        {toLandingLinks.map(({ href, label }) => (
          <a key={href} href={getResolveUrl(href)}>
            {t(label)}
          </a>
        ))}
        <div className="separator" />
      </>
    ),

    [t],
  );

  return (
    <div className={classes}>
      <Logo brandingLogos={brandingLogos} />

      {isLoggedIn ? (
        isSuperAdmin ? (
          <Link to={sellingLink.href}>
            <CollectionIcon />
            {t('deals')}
          </Link>
        ) : isDefaultSellingPortfolio(hasEnterprise, hasSellingDeals) ? (
          <DropdownsOnHoverInstance
            text={t('deals')}
            icon={CollectionIcon}
            links={dealsLinks}
            onTriggerAction={() => navigate(sellingLink.href)}
          />
        ) : (
          <Link to={buyingLink.href}>
            <CollectionIcon />
            {t('deals')}
          </Link>
        )
      ) : (
        RenderLandingLinks
      )}

      <DropdownsOnHoverInstance
        text={t('listings')}
        icon={BagIcon}
        links={headerListingLinks}
        onTriggerAction={() => navigate(businessLink.href)}
      />

      {isLoggedIn ? (
        <DropdownsOnHoverInstance
          text={t('solutions')}
          icon={SolutionsIcon}
          links={solutionsLinks}
          asExternal
        />
      ) : (
        <a href={getResolveUrl(newsLink.href)}>
          <NewsIcon />
          {t(newsLink.label)}
        </a>
      )}

      {hasSearch && <SearchForm />}

      <MessagingAndNotification hasMessagingLink={hasMessagingLink} />

      {isLoggedIn ? (
        <ProfileDropdown />
      ) : (
        <div className="auth-links">
          <Link to={LOGIN_ROUTE} className="ncoded-button ncoded-button--link">
            <LockclosedIcon />
            {t('signIn')}
          </Link>
          <Link
            to={SIGNUP_ROUTE}
            className="ncoded-button ncoded-button--solid"
          >
            <AddProfile />
            {t('join')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default ResolveHeaderDesktop;
